import React, { useState } from 'react';
import { ShieldAlert, X } from 'lucide-react';
import { toast } from 'react-toastify';

/**
 * One-time modal shown the first time an admin tries to use a feature that
 * may store sensitive customer data (insurance-ready invoices, intake forms
 * with health questions, structured charting, etc.).
 *
 * On confirm: calls `onAcknowledge()` (which the parent wires to
 * useSensitiveDataAck.acknowledge) and then `onClose()`. On cancel: closes
 * without acknowledging — the gated action is aborted by the parent.
 *
 * Props:
 *   - featureName: short label for the feature being enabled, e.g.
 *                  "insurance-ready invoice templates"
 *   - onAcknowledge: async () => void  — must POST the ack
 *   - onClose: () => void
 *   - onCancelled: optional () => void  — called if admin clicks Cancel
 */
export default function SensitiveDataDisclaimerModal({
  featureName,
  onAcknowledge,
  onClose,
  onCancelled,
}) {
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onAcknowledge();
      toast.success('Disclaimer acknowledged. Thanks.');
      onClose();
    } catch (e) {
      toast.error('Could not record acknowledgment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancelled) onCancelled();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <ShieldAlert className="text-amber-700" size={22} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Sensitive customer data — please read
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {featureName
                  ? <>You're about to enable <strong>{featureName}</strong>.</>
                  : "You're about to enable a feature that may store sensitive customer information."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4 text-sm text-gray-800 leading-relaxed">
          <p>
            These features can be used to store sensitive customer data
            including health information, government-issued identifiers,
            insurance details, or clinical notes.
          </p>
          <p>
            <strong>You are responsible</strong> for ensuring your hosting
            environment, data handling, and business practices comply with
            applicable privacy regulations for your jurisdiction. Examples
            include but are not limited to:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2 text-gray-700">
            <li><strong>Canada</strong> — PIPEDA (federal), PHIPA (Ontario), PIPA (Alberta / BC), HIA (Alberta health)</li>
            <li><strong>United States</strong> — HIPAA, HITECH, state-level analogues (CCPA / CPRA, etc.)</li>
            <li><strong>United Kingdom / EU</strong> — UK GDPR, EU GDPR</li>
            <li><strong>Australia</strong> — Privacy Act, APPs, My Health Records Act</li>
          </ul>
          <p>
            MorphBook is software you self-host on your own infrastructure.
            We do not provide regulatory compliance certification, audits,
            Business Associate Agreements (BAAs), Data Processing Addenda
            (DPAs), or warranty of compliance. Decisions about hosting
            location, encryption, backups, retention, breach notification,
            consent capture, and data minimization are yours to make and
            enforce.
          </p>
          <p>
            By acknowledging below, you confirm that you understand this and
            that responsibility for compliance rests with your business. Your
            acknowledgment is recorded in the audit log along with your user
            account and the time it occurred.
          </p>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? 'Recording…' : 'I understand and acknowledge'}
          </button>
        </div>
      </div>
    </div>
  );
}
